// =============================================================
// WebsiteAuditPage — Phase 3
// Comprehensive Website SEO Audit Results, Crawl Explorer, & History Filter
// Route: /businesses/:businessId/seo
// =============================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import {
  businessApi,
  Business,
  WebsiteAudit,
  WebsitePage,
  SEOFinding,
  SEOFindingCategory,
  SEOFindingSeverity,
} from '../lib/api';
import { IconClose } from '../components/icons';
import './WebsiteAuditPage.css';

// --- Local Crisp SVG Icons ---
function IconGlobeSvg({ style, className }: { style?: React.CSSProperties; className?: string }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

function IconExtLinkSvg({ style, className }: { style?: React.CSSProperties; className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

function IconFilterSvg({ style, className }: { style?: React.CSSProperties; className?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}

function IconRefreshSvg({ style, className }: { style?: React.CSSProperties; className?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
      <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
    </svg>
  );
}

function IconChevronDownSvg({ style, className }: { style?: React.CSSProperties; className?: string }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function IconEyeSvg({ style, className }: { style?: React.CSSProperties; className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function IconCheckSvg({ style, className }: { style?: React.CSSProperties; className?: string }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={style} className={className}>
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

// Category Icons
function IconTechSeoSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="8" rx="2" ry="2" />
      <rect x="2" y="14" width="20" height="8" rx="2" ry="2" />
      <line x1="6" y1="6" x2="6.01" y2="6" />
      <line x1="6" y1="18" x2="6.01" y2="18" />
    </svg>
  );
}

function IconOnPageSeoSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

function IconLocalSeoSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function IconContentSeoSvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  );
}

function IconIndexabilitySvg() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export default function WebsiteAuditPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const parsedBizId = businessId ? parseInt(businessId, 10) : undefined;

  const [business, setBusiness] = useState<Business | null>(null);
  const [audits, setAudits] = useState<WebsiteAudit[]>([]);
  const [selectedAuditId, setSelectedAuditId] = useState<number | null>(null);
  const [currentAudit, setCurrentAudit] = useState<WebsiteAudit | null>(null);
  const [pages, setPages] = useState<WebsitePage[]>([]);
  const [findings, setFindings] = useState<SEOFinding[]>([]);

  const [loading, setLoading] = useState(true);
  const [runningAudit, setRunningAudit] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [websiteOverride, setWebsiteOverride] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Filters for Main Findings View
  const [severityFilter, setSeverityFilter] = useState<'ALL' | SEOFindingSeverity>('ALL');
  const [categoryFilter, setCategoryFilter] = useState<'ALL' | SEOFindingCategory>('ALL');
  const [activeTab, setActiveTab] = useState<'findings' | 'pages'>('findings');

  // Page Details Inspection Modal
  const [selectedPage, setSelectedPage] = useState<WebsitePage | null>(null);

  // Date Range Filter Modal State
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [presetRange, setPresetRange] = useState<'all' | '7d' | '30d' | '90d'>('all');
  const [modalAudits, setModalAudits] = useState<WebsiteAudit[]>([]);
  const [loadingModalAudits, setLoadingModalAudits] = useState(false);

  // Custom Audit Selector Dropdown State
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownTab, setDropdownTab] = useState<'completed' | 'all'>('completed');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    }
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const displayedDropdownAudits = useMemo(() => {
    if (dropdownTab === 'completed') {
      const completedOnly = audits.filter((a) => a.status === 'COMPLETED');
      return completedOnly.length > 0 ? completedOnly : audits;
    }
    return audits;
  }, [audits, dropdownTab]);

  // 1. Initial Load: Business & Audit History
  const loadInitialData = useCallback(async () => {
    if (!parsedBizId) return;
    try {
      setLoading(true);
      setError(null);

      const [bizData, auditList] = await Promise.all([
        businessApi.get(parsedBizId),
        businessApi.listAudits(parsedBizId, { limit: 100 }),
      ]);

      setBusiness(bizData);
      setAudits(auditList);
      setModalAudits(auditList);
      setWebsiteOverride(bizData.website ?? '');

      if (auditList.length > 0) {
        setSelectedAuditId(auditList[0].id);
      }
    } catch (err: unknown) {
      console.error('Failed to load audit page', err);
      const msg = err instanceof Error ? err.message : 'Failed to load business data';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [parsedBizId]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (parsedBizId) {
      localStorage.setItem('active_dashboard_business_id', String(parsedBizId));
      window.dispatchEvent(new CustomEvent('active-business-change', { detail: parsedBizId }));
    }
  }, [parsedBizId]);

  // 2. Load Selected Audit Details (Pages & Findings)
  const loadAuditDetails = useCallback(async (auditId: number) => {
    if (!parsedBizId) return;
    try {
      const [auditData, pageList, findingList] = await Promise.all([
        businessApi.getAudit(parsedBizId, auditId),
        businessApi.getAuditPages(parsedBizId, auditId),
        businessApi.getAuditFindings(parsedBizId, auditId),
      ]);

      setCurrentAudit(auditData);
      setPages(pageList);
      setFindings(findingList);
    } catch (err) {
      console.error('Failed to load audit details', err);
    }
  }, [parsedBizId]);

  useEffect(() => {
    if (selectedAuditId) {
      loadAuditDetails(selectedAuditId);
    }
  }, [selectedAuditId, loadAuditDetails]);

  // 3. Auto-poll if current audit is running (QUEUED, CRAWLING, ANALYZING)
  useEffect(() => {
    if (!currentAudit) return;
    const isRunning =
      currentAudit.status === 'QUEUED' ||
      currentAudit.status === 'CRAWLING' ||
      currentAudit.status === 'ANALYZING';

    if (!isRunning || !selectedAuditId) return;

    const interval = setInterval(() => {
      loadAuditDetails(selectedAuditId);
      if (parsedBizId) {
        businessApi.listAudits(parsedBizId, { limit: 100 }).then((res) => {
          setAudits(res);
          setModalAudits(res);
        }).catch(() => {});
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [currentAudit, selectedAuditId, loadAuditDetails, parsedBizId]);

  // Trigger New Audit
  async function handleStartAudit() {
    if (!parsedBizId) return;
    try {
      setRunningAudit(true);
      setError(null);
      setShowConfirmModal(false);

      const target = websiteOverride.trim() || undefined;
      const started = await businessApi.startAudit(parsedBizId, target);

      // Refresh audits list and select newly created audit
      const auditList = await businessApi.listAudits(parsedBizId, { limit: 100 });
      setAudits(auditList);
      setModalAudits(auditList);
      setSelectedAuditId(started.id);
      setCurrentAudit(started);
      setPages([]);
      setFindings([]);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to start website audit';
      setError(msg);
    } finally {
      setRunningAudit(false);
    }
  }

  // Cancel Running Audit
  async function handleCancelAudit() {
    if (!parsedBizId || !selectedAuditId) return;
    try {
      setCancelling(true);
      await businessApi.cancelAudit(parsedBizId, selectedAuditId);
      loadAuditDetails(selectedAuditId);
      const auditList = await businessApi.listAudits(parsedBizId, { limit: 100 });
      setAudits(auditList);
      setModalAudits(auditList);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel audit';
      setError(msg);
    } finally {
      setCancelling(false);
    }
  }

  // Filtered Audits by Date Range in Popup Modal
  const fetchFilteredAudits = useCallback(async (start?: string, end?: string) => {
    if (!parsedBizId) return;
    try {
      setLoadingModalAudits(true);
      const res = await businessApi.listAudits(parsedBizId, {
        startDate: start || undefined,
        endDate: end || undefined,
        limit: 100,
      });
      setModalAudits(res);
    } catch (e) {
      console.warn('Fallback to client-side audit filter', e);
      let list = [...audits];
      if (start) {
        const sTime = new Date(start).getTime();
        list = list.filter((a) => new Date(a.createdAt).getTime() >= sTime);
      }
      if (end) {
        const eTime = new Date(end).setHours(23, 59, 59, 999);
        list = list.filter((a) => new Date(a.createdAt).getTime() <= eTime);
      }
      setModalAudits(list);
    } finally {
      setLoadingModalAudits(false);
    }
  }, [parsedBizId, audits]);

  const handleApplyPreset = (preset: 'all' | '7d' | '30d' | '90d') => {
    setPresetRange(preset);
    const today = new Date();
    const endStr = today.toISOString().split('T')[0];

    if (preset === 'all') {
      setFilterStartDate('');
      setFilterEndDate('');
      fetchFilteredAudits('', '');
      return;
    }

    const pastDate = new Date();
    if (preset === '7d') pastDate.setDate(today.getDate() - 7);
    if (preset === '30d') pastDate.setDate(today.getDate() - 30);
    if (preset === '90d') pastDate.setDate(today.getDate() - 90);

    const startStr = pastDate.toISOString().split('T')[0];
    setFilterStartDate(startStr);
    setFilterEndDate(endStr);
    fetchFilteredAudits(startStr, endStr);
  };

  const handleManualDateChange = (start: string, end: string) => {
    setFilterStartDate(start);
    setFilterEndDate(end);
    setPresetRange('all');
    fetchFilteredAudits(start, end);
  };

  const handleResetFilter = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setPresetRange('all');
    fetchFilteredAudits('', '');
  };

  const handleSelectAuditFromModal = (auditId: number) => {
    setSelectedAuditId(auditId);
    setShowFilterModal(false);
  };

  // Filtered Findings on the page
  const filteredFindings = useMemo(() => {
    return findings.filter((f) => {
      if (severityFilter !== 'ALL' && f.severity !== severityFilter) return false;
      if (categoryFilter !== 'ALL' && f.category !== categoryFilter) return false;
      return true;
    });
  }, [findings, severityFilter, categoryFilter]);

  const errorCount = findings.filter((f) => f.severity === 'ERROR').length;
  const warningCount = findings.filter((f) => f.severity === 'WARNING').length;
  const infoCount = findings.filter((f) => f.severity === 'INFO').length;

  const hasActiveFilter = !!(filterStartDate || filterEndDate);

  if (loading) {
    return (
      <DashboardLayout title="Website SEO Audit" subtitle="Loading analysis...">
        <div className="card" style={{ padding: 64, textAlign: 'center', color: '#64748b' }}>
          <div className="progress-spinner" style={{ margin: '0 auto 16px auto', width: 36, height: 36 }} />
          <h3 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>Loading Audit Records</h3>
          <p style={{ fontSize: '0.875rem', color: '#64748b' }}>Retrieving crawl history and deterministic scoring data...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error && !business) {
    return (
      <DashboardLayout title="Error" subtitle="Audit unavailable">
        <div className="card" style={{ padding: 48, textAlign: 'center', maxWidth: 540, margin: '40px auto' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Unable to Load Audit</h2>
          <p style={{ color: '#64748b', marginBottom: 24, lineHeight: 1.5 }}>{error}</p>
          <Link to="/businesses" className="btn btn-primary">
            Back to Businesses
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const isAuditInProgress =
    currentAudit?.status === 'QUEUED' ||
    currentAudit?.status === 'CRAWLING' ||
    currentAudit?.status === 'ANALYZING';

  return (
    <DashboardLayout
      title={`${business?.name ?? 'Business'} — SEO Audit`}
      subtitle="Deterministic on-page crawl & local search readiness evaluation"
      breadcrumbs={[
        { label: 'Dashboard', to: '/dashboard' },
        { label: 'Businesses', to: '/businesses' },
        { label: business?.name ?? 'Business', to: `/businesses/${business?.id}/dashboard` },
        { label: 'Website SEO' },
      ]}
    >
      {/* Top Action & History Bar */}
      <div className="audit-action-header-v2">
        <div className="audit-target-hero">
          <div className="audit-globe-avatar">
            <IconGlobeSvg style={{ width: 24, height: 24 }} />
          </div>
          <div className="audit-target-details">
            <div className="audit-target-url-wrap">
              <a
                href={business?.website ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="audit-target-url"
                title="Open audited website in new tab"
              >
                {business?.website ?? 'No website configured'}
              </a>
              {business?.website && (
                <a
                  href={business.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="audit-target-ext-link"
                  title="Open site"
                >
                  <IconExtLinkSvg />
                </a>
              )}
            </div>
            <span className="audit-target-subtext">
              Audit engine evaluates up to 50 public pages under safe concurrency and deterministic rules.
            </span>
          </div>
        </div>

        <div className="audit-header-actions-v2">
          {audits.length > 0 && (
            <div className="audit-custom-dropdown-container" ref={dropdownRef}>
              {(() => {
                const selectedAudit = audits.find((a) => a.id === selectedAuditId) ?? audits[0];
                const isLatest = selectedAudit?.id === audits[0]?.id;
                const score = selectedAudit?.overallScore;
                const scoreClass =
                  score !== null && score !== undefined && score >= 90
                    ? 'score-high'
                    : score !== null && score !== undefined && score >= 70
                    ? 'score-med'
                    : 'score-low';

                return (
                  <button
                    id="audit-history-selector"
                    type="button"
                    className={`audit-selector-trigger ${showDropdown ? 'is-active' : ''}`}
                    onClick={() => setShowDropdown((prev) => !prev)}
                    title="Switch audit report"
                    aria-haspopup="listbox"
                    aria-expanded={showDropdown}
                  >
                    <div className="audit-trigger-left">
                      <span
                        className={`audit-trigger-dot status-${selectedAudit?.status?.toLowerCase() ?? 'completed'}`}
                      />
                      <span className="audit-trigger-title">Audit #{selectedAudit?.auditNumber ?? selectedAudit?.id}</span>
                      {isLatest && <span className="audit-trigger-badge-latest">LATEST</span>}
                      {score !== null && score !== undefined && (
                        <span className={`audit-trigger-score-pill ${scoreClass}`}>
                          Score: {score}
                        </span>
                      )}
                      <span className="audit-trigger-date">
                        {selectedAudit?.createdAt
                          ? `${new Date(selectedAudit.createdAt).toLocaleDateString(undefined, {
                              month: 'numeric',
                              day: 'numeric',
                            })} · ${new Date(selectedAudit.createdAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}`
                          : ''}
                      </span>
                    </div>
                    <span className={`audit-trigger-chevron ${showDropdown ? 'is-flipped' : ''}`}>
                      <IconChevronDownSvg />
                    </span>
                  </button>
                );
              })()}

              {/* Styled Popover Dropdown Menu */}
              {showDropdown && (
                <div className="audit-dropdown-menu" role="listbox">
                  <div className="audit-dropdown-header">
                    <div className="audit-dropdown-header-top">
                      <span className="audit-dropdown-heading">Audit Reports</span>
                      <span className="audit-dropdown-counter">{audits.length} runs</span>
                    </div>
                    <div className="audit-dropdown-filter-tabs">
                      <button
                        type="button"
                        className={`audit-tab-chip ${dropdownTab === 'completed' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDropdownTab('completed');
                        }}
                      >
                        Completed ({audits.filter((a) => a.status === 'COMPLETED').length})
                      </button>
                      <button
                        type="button"
                        className={`audit-tab-chip ${dropdownTab === 'all' ? 'active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDropdownTab('all');
                        }}
                      >
                        All Runs ({audits.length})
                      </button>
                    </div>
                  </div>

                  <div className="audit-dropdown-list">
                    {displayedDropdownAudits.map((a) => {
                      const isItemActive = a.id === selectedAuditId;
                      const isItemLatest = a.id === audits[0]?.id;
                      const itemScoreClass =
                        a.overallScore !== null && a.overallScore >= 90
                          ? 'score-high'
                          : a.overallScore !== null && a.overallScore >= 70
                          ? 'score-med'
                          : 'score-low';

                      return (
                        <div
                          key={a.id}
                          role="option"
                          aria-selected={isItemActive}
                          className={`audit-dropdown-item ${isItemActive ? 'is-selected' : ''}`}
                          onClick={() => {
                            setSelectedAuditId(a.id);
                            setShowDropdown(false);
                          }}
                        >
                          <div className="audit-item-score-box">
                            {a.overallScore !== null ? (
                              <div className={`audit-item-score ${itemScoreClass}`}>
                                <span className="score-num">{a.overallScore}</span>
                                <span className="score-denom">/100</span>
                              </div>
                            ) : (
                              <span className={`audit-item-status status-${a.status.toLowerCase()}`}>
                                {a.status === 'FAILED' ? 'FAIL' : a.status.slice(0, 4)}
                              </span>
                            )}
                          </div>

                          <div className="audit-item-meta">
                            <div className="audit-item-top">
                              <span className="audit-item-name">Audit #{a.auditNumber ?? a.id}</span>
                              {isItemLatest && <span className="audit-item-latest-tag">LATEST</span>}
                              <span className={`audit-item-badge status-${a.status.toLowerCase()}`}>
                                {a.status}
                              </span>
                            </div>
                            <div className="audit-item-sub">
                              <span>
                                {new Date(a.createdAt).toLocaleDateString(undefined, {
                                  month: 'short',
                                  day: 'numeric',
                                })}
                                {' · '}
                                {new Date(a.createdAt).toLocaleTimeString([], {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </span>
                              <span className="audit-item-sep">•</span>
                              <span>{a.pagesCrawled} pages</span>
                            </div>
                          </div>

                          {isItemActive && (
                            <div className="audit-item-check">
                              <IconCheckSvg />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="audit-dropdown-footer">
                    <button
                      type="button"
                      className="audit-dropdown-footer-btn"
                      onClick={() => {
                        setShowDropdown(false);
                        setShowFilterModal(true);
                        fetchFilteredAudits(filterStartDate, filterEndDate);
                      }}
                    >
                      <IconFilterSvg />
                      <span>Date Range Filter & Full Table ({audits.length} reports) →</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Apply Filter Button - Always Available */}
          <button
            id="apply-audit-filter-btn"
            type="button"
            className={`btn-filter-trigger ${hasActiveFilter ? 'has-active-filter' : ''}`}
            onClick={() => {
              setShowFilterModal(true);
              fetchFilteredAudits(filterStartDate, filterEndDate);
            }}
            title="Filter audit reports by date range"
          >
            <IconFilterSvg />
            <span>Apply Filter</span>
            {hasActiveFilter ? (
              <span className="filter-badge-count">Active</span>
            ) : (
              <span className="filter-badge-count">{audits.length}</span>
            )}
          </button>

          {/* Re-Run SEO Audit Button */}
          <button
            id="start-audit-modal-btn"
            type="button"
            className="btn-rerun-audit"
            onClick={() => setShowConfirmModal(true)}
            disabled={runningAudit || isAuditInProgress}
          >
            <IconRefreshSvg className={runningAudit || isAuditInProgress ? 'spin-icon' : ''} />
            <span>
              {runningAudit
                ? 'Starting Crawl...'
                : isAuditInProgress
                ? 'Audit Running...'
                : audits.length === 0
                ? 'Start First SEO Audit'
                : 'Re-Run SEO Audit'}
            </span>
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 20 }}>
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* In-Progress Notification Banner */}
      {isAuditInProgress && (
        <div className="audit-progress-banner">
          <div className="progress-spinner" />
          <div className="progress-content">
            <h3 className="progress-title">
              Audit in progress: {currentAudit?.status}
            </h3>
            <p className="progress-desc">
              {currentAudit?.status === 'QUEUED' && 'Waiting for background crawler worker to initialize queue...'}
              {currentAudit?.status === 'CRAWLING' &&
                `Crawling internal links safely with polite delay (${pages.length} pages indexed so far)...`}
              {currentAudit?.status === 'ANALYZING' &&
                'Executing deterministic on-page & local SEO rules across all crawled pages...'}
            </p>
          </div>
          <button
            className="btn btn-secondary btn-sm"
            onClick={handleCancelAudit}
            disabled={cancelling}
          >
            {cancelling ? 'Cancelling...' : 'Cancel Crawl'}
          </button>
        </div>
      )}

      {/* No Audits Yet State */}
      {audits.length === 0 && !isAuditInProgress && (
        <div className="card empty-audit-state">
          <div className="empty-audit-icon">🔍</div>
          <h2 className="empty-audit-title">No Website SEO Audits Yet</h2>
          <p className="empty-audit-desc">
            Run an in-depth deterministic audit to scan your title tags, meta descriptions,
            H1/H2 structures, image alt attributes, schema.org markup, and local NAP consistency.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => setShowConfirmModal(true)}
          >
            Start Website SEO Audit
          </button>
        </div>
      )}

      {/* Active Audit Results Display */}
      {currentAudit && currentAudit.status === 'COMPLETED' && (
        <>
          {/* Hero Score & Category Breakdown */}
          <div className="audit-score-hero">
            {/* Overall Score Circle */}
            <div className="score-gauge-card">
              <div className="score-number-display">
                {currentAudit.overallScore ?? 0}
                <span className="score-max">/100</span>
              </div>
              <div className="score-label">Overall SEO Score</div>
              <div
                className={`score-rating-tag ${
                  (currentAudit.overallScore ?? 0) >= 80
                    ? 'excellent'
                    : (currentAudit.overallScore ?? 0) >= 60
                    ? 'fair'
                    : 'poor'
                }`}
              >
                {(currentAudit.overallScore ?? 0) >= 80
                  ? 'Strong Foundation'
                  : (currentAudit.overallScore ?? 0) >= 60
                  ? 'Needs Optimization'
                  : 'Action Required'}
              </div>
              <span className="score-transparency-note">
                100% deterministic rule-based score
              </span>
            </div>

            {/* Category Score Cards Grid with Sharp SVG Icons */}
            <div className="category-scores-grid">
              {/* Technical SEO */}
              <div className="category-card-v2">
                <div className="category-header-v2">
                  <div className="category-icon-box tech">
                    <IconTechSeoSvg />
                  </div>
                  <span className="category-name-v2">Technical SEO</span>
                </div>
                <div className="category-points">
                  {currentAudit.technicalScore ?? 0} <span>/ 25</span>
                </div>
                <div className="category-bar">
                  <div
                    className="category-fill"
                    style={{ width: `${((currentAudit.technicalScore ?? 0) / 25) * 100}%` }}
                  />
                </div>
                <span className="category-note">HTTPS, Response Time, Canonicals</span>
              </div>

              {/* On-Page SEO */}
              <div className="category-card-v2">
                <div className="category-header-v2">
                  <div className="category-icon-box onpage">
                    <IconOnPageSeoSvg />
                  </div>
                  <span className="category-name-v2">On-Page SEO</span>
                </div>
                <div className="category-points">
                  {currentAudit.onPageScore ?? 0} <span>/ 25</span>
                </div>
                <div className="category-bar">
                  <div
                    className="category-fill"
                    style={{ width: `${((currentAudit.onPageScore ?? 0) / 25) * 100}%` }}
                  />
                </div>
                <span className="category-note">Titles, Meta Descriptions, H1s</span>
              </div>

              {/* Local SEO */}
              <div className="category-card-v2">
                <div className="category-header-v2">
                  <div className="category-icon-box local">
                    <IconLocalSeoSvg />
                  </div>
                  <span className="category-name-v2">Local SEO</span>
                </div>
                <div className="category-points">
                  {currentAudit.localScore ?? 0} <span>/ 25</span>
                </div>
                <div className="category-bar">
                  <div
                    className="category-fill"
                    style={{ width: `${((currentAudit.localScore ?? 0) / 25) * 100}%` }}
                  />
                </div>
                <span className="category-note">Local NAP, Schema, City Keywords</span>
              </div>

              {/* Content & Media */}
              <div className="category-card-v2">
                <div className="category-header-v2">
                  <div className="category-icon-box content">
                    <IconContentSeoSvg />
                  </div>
                  <span className="category-name-v2">Content & Media</span>
                </div>
                <div className="category-points">
                  {currentAudit.contentScore ?? 0} <span>/ 15</span>
                </div>
                <div className="category-bar">
                  <div
                    className="category-fill"
                    style={{ width: `${((currentAudit.contentScore ?? 0) / 15) * 100}%` }}
                  />
                </div>
                <span className="category-note">Image Alt Tags, Thin Content</span>
              </div>

              {/* Indexability */}
              <div className="category-card-v2">
                <div className="category-header-v2">
                  <div className="category-icon-box index">
                    <IconIndexabilitySvg />
                  </div>
                  <span className="category-name-v2">Indexability</span>
                </div>
                <div className="category-points">
                  {currentAudit.indexabilityScore ?? 0} <span>/ 10</span>
                </div>
                <div className="category-bar">
                  <div
                    className="category-fill"
                    style={{ width: `${((currentAudit.indexabilityScore ?? 0) / 10) * 100}%` }}
                  />
                </div>
                <span className="category-note">Viewport, Language, Open Graph</span>
              </div>
            </div>
          </div>

          {/* Audit Metrics Banner */}
          <div className="audit-meta-summary-bar">
            <div className="meta-stat">
              <span className="meta-k">Pages Crawled:</span>
              <span className="meta-v font-semibold">{currentAudit.pagesCrawled}</span>
            </div>
            <div className="meta-stat">
              <span className="meta-k">Errors:</span>
              <span className="meta-v tag-err">{errorCount}</span>
            </div>
            <div className="meta-stat">
              <span className="meta-k">Warnings:</span>
              <span className="meta-v tag-warn">{warningCount}</span>
            </div>
            <div className="meta-stat">
              <span className="meta-k">Passed Checks:</span>
              <span className="meta-v tag-info">{infoCount}</span>
            </div>
            <div className="meta-stat" style={{ marginLeft: 'auto' }}>
              <span className="meta-k">Completed:</span>
              <span className="meta-v">
                {currentAudit.completedAt
                  ? new Date(currentAudit.completedAt).toLocaleString()
                  : 'Just now'}
              </span>
            </div>
          </div>

          {/* Tab Navigation: Findings vs Pages */}
          <div className="audit-tab-bar">
            <button
              className={`audit-tab-btn ${activeTab === 'findings' ? 'active' : ''}`}
              onClick={() => setActiveTab('findings')}
            >
              SEO Findings ({findings.length})
            </button>
            <button
              className={`audit-tab-btn ${activeTab === 'pages' ? 'active' : ''}`}
              onClick={() => setActiveTab('pages')}
            >
              Crawled Pages Explorer ({pages.length})
            </button>
          </div>

          {/* TAB 1: SEO FINDINGS */}
          {activeTab === 'findings' && (
            <div className="findings-section">
              {/* Filter controls */}
              <div className="findings-filter-row">
                <div className="filter-pill-group">
                  <button
                    className={`filter-pill ${severityFilter === 'ALL' ? 'active' : ''}`}
                    onClick={() => setSeverityFilter('ALL')}
                  >
                    All ({findings.length})
                  </button>
                  <button
                    className={`filter-pill error ${severityFilter === 'ERROR' ? 'active' : ''}`}
                    onClick={() => setSeverityFilter('ERROR')}
                  >
                    Errors ({errorCount})
                  </button>
                  <button
                    className={`filter-pill warning ${severityFilter === 'WARNING' ? 'active' : ''}`}
                    onClick={() => setSeverityFilter('WARNING')}
                  >
                    Warnings ({warningCount})
                  </button>
                  <button
                    className={`filter-pill info ${severityFilter === 'INFO' ? 'active' : ''}`}
                    onClick={() => setSeverityFilter('INFO')}
                  >
                    Passed / Info ({infoCount})
                  </button>
                </div>

                <select
                  className="form-select filter-select"
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value as any)}
                >
                  <option value="ALL">All Categories</option>
                  <option value="TECHNICAL">Technical SEO</option>
                  <option value="ON_PAGE">On-Page SEO</option>
                  <option value="LOCAL">Local SEO</option>
                  <option value="CONTENT">Content & Media</option>
                  <option value="INDEXABILITY">Indexability</option>
                </select>
              </div>

              {/* Findings Cards List */}
              <div className="findings-cards-list">
                {filteredFindings.length === 0 ? (
                  <div className="card" style={{ padding: 32, textAlign: 'center', color: '#64748b' }}>
                    No findings match the selected filters.
                  </div>
                ) : (
                  filteredFindings.map((finding) => (
                    <div
                      key={finding.id}
                      className={`card finding-card severity-${finding.severity.toLowerCase()}`}
                    >
                      <div className="finding-card-header">
                        <div className="finding-badge-wrap">
                          <span className={`badge badge-severity ${finding.severity.toLowerCase()}`}>
                            {finding.severity}
                          </span>
                          <span className="badge badge-gray category-badge">
                            {finding.category.replace('_', ' ')}
                          </span>
                          <span className="finding-code-pill">{finding.code}</span>
                        </div>
                      </div>

                      <h4 className="finding-title">{finding.title}</h4>
                      <p className="finding-description">{finding.description}</p>

                      {finding.page && (
                        <div className="finding-page-link">
                          <span className="link-lbl">Affected URL:</span>
                          <a
                            href={finding.page.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="page-url-link"
                          >
                            {finding.page.url}
                          </a>
                        </div>
                      )}

                      <div className="finding-recommendation-box">
                        <span className="rec-title">💡 Actionable Recommendation:</span>
                        <p className="rec-text">{finding.recommendation}</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* TAB 2: CRAWLED PAGES EXPLORER */}
          {activeTab === 'pages' && (
            <div className="pages-section card">
              <div className="table-responsive">
                <table className="pages-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>URL</th>
                      <th>Title</th>
                      <th>Depth</th>
                      <th>H1</th>
                      <th>Words</th>
                      <th>Alt Missing</th>
                      <th>Response</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.map((p) => (
                      <tr key={p.id}>
                        <td>
                          <span
                            className={`badge ${
                              p.statusCode >= 200 && p.statusCode < 300
                                ? 'badge-green'
                                : p.statusCode >= 300 && p.statusCode < 400
                                ? 'badge-blue'
                                : 'badge-red'
                            }`}
                          >
                            {p.statusCode || 'ERR'}
                          </span>
                        </td>
                        <td className="page-url-cell">
                          <a href={p.url} target="_blank" rel="noopener noreferrer">
                            {p.url}
                          </a>
                        </td>
                        <td className="page-title-cell">{p.title || ''}</td>
                        <td>{p.depth}</td>
                        <td>{p.h1Count}</td>
                        <td>{p.wordCount}</td>
                        <td>
                          {p.missingAltCount > 0 ? (
                            <span className="tag-err font-semibold">
                              {p.missingAltCount} / {p.imageCount}
                            </span>
                          ) : (
                            <span className="tag-info">{p.imageCount}</span>
                          )}
                        </td>
                        <td>{p.responseTime ? `${p.responseTime}ms` : ''}</td>
                        <td>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => setSelectedPage(p)}
                          >
                            Inspect
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* =============================================================
          MODAL: DATE RANGE FILTER POPUP & REPORT HISTORY TABLE
          ============================================================= */}
      {showFilterModal && (
        <div className="audit-filter-modal-backdrop" onClick={() => setShowFilterModal(false)}>
          <div
            className="audit-filter-modal-box"
            role="dialog"
            aria-modal="true"
            aria-labelledby="audit-filter-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="audit-filter-modal-header">
              <div>
                <h3 id="audit-filter-modal-title" className="audit-filter-modal-title">
                  Audit Reports History & Filter
                </h3>
                <p className="audit-filter-modal-subtitle">
                  Filter historical crawls by date range, compare scores, and load detailed reports.
                </p>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowFilterModal(false)}
                title="Close modal"
              >
                <IconClose style={{ width: 22, height: 22 }} />
              </button>
            </div>

            {/* Filter Controls Bar */}
            <div className="audit-filter-controls-bar">
              {/* Preset Chips */}
              <div className="filter-preset-chips">
                <span className="filter-input-label">Quick Presets:</span>
                <button
                  type="button"
                  className={`preset-chip-btn ${presetRange === 'all' && !filterStartDate && !filterEndDate ? 'active' : ''}`}
                  onClick={() => handleApplyPreset('all')}
                >
                  All Time
                </button>
                <button
                  type="button"
                  className={`preset-chip-btn ${presetRange === '7d' ? 'active' : ''}`}
                  onClick={() => handleApplyPreset('7d')}
                >
                  Last 7 Days
                </button>
                <button
                  type="button"
                  className={`preset-chip-btn ${presetRange === '30d' ? 'active' : ''}`}
                  onClick={() => handleApplyPreset('30d')}
                >
                  Last 30 Days
                </button>
                <button
                  type="button"
                  className={`preset-chip-btn ${presetRange === '90d' ? 'active' : ''}`}
                  onClick={() => handleApplyPreset('90d')}
                >
                  Last 90 Days
                </button>
              </div>

              {/* Date Input Pickers */}
              <div className="filter-date-inputs-row">
                <div className="filter-input-group">
                  <label className="filter-input-label" htmlFor="audit-start-date">
                    From:
                  </label>
                  <input
                    id="audit-start-date"
                    type="date"
                    className="filter-date-input"
                    value={filterStartDate}
                    onChange={(e) => handleManualDateChange(e.target.value, filterEndDate)}
                  />
                </div>

                <div className="filter-input-group">
                  <label className="filter-input-label" htmlFor="audit-end-date">
                    To:
                  </label>
                  <input
                    id="audit-end-date"
                    type="date"
                    className="filter-date-input"
                    value={filterEndDate}
                    onChange={(e) => handleManualDateChange(filterStartDate, e.target.value)}
                  />
                </div>

                <div className="filter-actions-right">
                  {hasActiveFilter && (
                    <button
                      type="button"
                      className="btn-filter-reset"
                      onClick={handleResetFilter}
                    >
                      Clear Dates
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => fetchFilteredAudits(filterStartDate, filterEndDate)}
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Body: Reports Table */}
            <div className="audit-filter-modal-content">
              {loadingModalAudits ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>
                  <div className="progress-spinner" style={{ margin: '0 auto 12px auto' }} />
                  Loading audits for selected date range...
                </div>
              ) : modalAudits.length === 0 ? (
                <div style={{ padding: 48, textAlign: 'center', color: '#64748b' }}>
                  <div style={{ fontSize: '2rem', marginBottom: 12 }}>📅</div>
                  <h4 style={{ fontWeight: 600, color: '#334155', marginBottom: 4 }}>
                    No audit reports found
                  </h4>
                  <p style={{ fontSize: '0.85rem' }}>
                    No crawls were performed within the selected date range. Try widening your dates.
                  </p>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ marginTop: 12 }}
                    onClick={handleResetFilter}
                  >
                    Show All Audits
                  </button>
                </div>
              ) : (
                <table className="audit-reports-table">
                  <thead>
                    <tr>
                      <th>Audit Run</th>
                      <th>Date & Time</th>
                      <th>Overall Score</th>
                      <th>Pages</th>
                      <th>Status</th>
                      <th>Findings</th>
                      <th style={{ textAlign: 'right' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modalAudits.map((a, idx) => {
                      const isSelected = selectedAuditId === a.id;
                      const isLatest = idx === 0 && !hasActiveFilter;
                      const score = a.overallScore;

                      let scoreClass = 'none';
                      if (score !== null) {
                        if (score >= 80) scoreClass = 'excellent';
                        else if (score >= 60) scoreClass = 'fair';
                        else scoreClass = 'poor';
                      }

                      let statusClass = 'completed';
                      if (a.status === 'FAILED') statusClass = 'failed';
                      else if (a.status === 'QUEUED' || a.status === 'CRAWLING' || a.status === 'ANALYZING') {
                        statusClass = 'running';
                      }

                      return (
                        <tr key={a.id} className={isSelected ? 'active-audit-row' : ''}>
                          <td>
                            <div className="audit-id-badge">
                              <span>#{a.auditNumber ?? a.id}</span>
                              {isLatest && <span className="tag-latest-pill">Latest</span>}
                            </div>
                          </td>
                          <td>
                            <div style={{ fontWeight: 500, color: '#1e293b' }}>
                              {new Date(a.createdAt).toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              {new Date(a.createdAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </td>
                          <td>
                            {score !== null ? (
                              <div className={`score-cell-pill ${scoreClass}`}>
                                <span>{score}</span>
                                <span style={{ fontSize: '0.72rem', opacity: 0.8 }}>/100</span>
                              </div>
                            ) : (
                              <span style={{ color: '#94a3b8', fontSize: '0.82rem' }}>—</span>
                            )}
                          </td>
                          <td>
                            <span style={{ fontWeight: 600 }}>{a.pagesCrawled}</span>
                            <span style={{ color: '#64748b', fontSize: '0.8rem' }}> pgs</span>
                          </td>
                          <td>
                            <span className={`audit-status-chip ${statusClass}`}>
                              {a.status}
                            </span>
                          </td>
                          <td>
                            <span style={{ fontSize: '0.82rem', color: '#475569' }}>
                              {a._count?.findings !== undefined ? `${a._count.findings} findings` : '—'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {isSelected ? (
                              <button
                                type="button"
                                className="btn-view-report is-active-btn"
                                disabled
                              >
                                <IconCheckSvg /> Active Now
                              </button>
                            ) : (
                              <button
                                type="button"
                                className="btn-view-report"
                                onClick={() => handleSelectAuditFromModal(a.id)}
                              >
                                <IconEyeSvg /> View Report
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Modal Footer */}
            <div className="audit-filter-modal-footer">
              <span className="modal-result-count">
                Showing {modalAudits.length} {modalAudits.length === 1 ? 'report' : 'reports'}
                {hasActiveFilter && ' (filtered)'}
              </span>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => setShowFilterModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CONFIRMATION MODAL BEFORE RUNNING AUDIT */}
      {showConfirmModal && (
        <div className="modal-backdrop">
          <div className="modal-box" role="dialog" aria-modal="true">
            <div className="modal-header">
              <h3 className="modal-title">Run Website SEO Audit</h3>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setShowConfirmModal(false)}
              >
                <IconClose style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <div className="modal-body-content">
              <p style={{ color: '#475569', marginBottom: 16, lineHeight: 1.5 }}>
                This will crawl up to <strong>50 public pages</strong> on the website,
                extracting title tags, meta descriptions, heading structures, image alt attributes,
                and local NAP signals.
              </p>

              <div className="form-group">
                <label className="form-label" htmlFor="audit-url-input">
                  Target Website URL
                </label>
                <input
                  id="audit-url-input"
                  type="text"
                  className="form-input"
                  value={websiteOverride}
                  onChange={(e) => setWebsiteOverride(e.target.value)}
                  placeholder="https://example.com"
                />
                <span className="form-hint">
                  Crawler only follows internal links and respects robots.txt.
                </span>
              </div>

              <div className="safety-notice-box">
                <span style={{ fontSize: '1rem' }}>🛡️</span>
                <span style={{ fontSize: '0.78rem', color: '#0369a1' }}>
                  <strong>Safety Guarantee:</strong> Crawler will never crawl private IPs, localhost,
                  or authentication-protected endpoints.
                </span>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowConfirmModal(false)}
                disabled={runningAudit}
              >
                Cancel
              </button>
              <button
                type="button"
                id="confirm-start-audit-btn"
                className="btn btn-primary"
                onClick={handleStartAudit}
                disabled={runningAudit || !websiteOverride.trim()}
              >
                {runningAudit ? 'Initializing...' : 'Start Crawl & Audit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PAGE DETAILS INSPECT MODAL */}
      {selectedPage && (
        <div className="modal-backdrop">
          <div className="modal-box page-inspect-modal" role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <h3 className="modal-title">Page Metadata Inspection</h3>
                <span className="modal-subtitle-url">{selectedPage.url}</span>
              </div>
              <button
                type="button"
                className="modal-close-btn"
                onClick={() => setSelectedPage(null)}
              >
                <IconClose style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <div className="inspect-modal-body">
              <div className="inspect-grid">
                <div className="inspect-item">
                  <span className="inspect-k">HTTP Status:</span>
                  <span className="inspect-v font-semibold">{selectedPage.statusCode}</span>
                </div>
                <div className="inspect-item">
                  <span className="inspect-k">Response Time:</span>
                  <span className="inspect-v">{selectedPage.responseTime}ms</span>
                </div>
                <div className="inspect-item">
                  <span className="inspect-k">Crawl Depth:</span>
                  <span className="inspect-v">{selectedPage.depth}</span>
                </div>
                <div className="inspect-item">
                  <span className="inspect-k">Word Count:</span>
                  <span className="inspect-v">{selectedPage.wordCount} words</span>
                </div>
                <div className="inspect-item">
                  <span className="inspect-k">Images:</span>
                  <span className="inspect-v">
                    {selectedPage.imageCount} total ({selectedPage.missingAltCount} missing alt)
                  </span>
                </div>
                <div className="inspect-item">
                  <span className="inspect-k">Links:</span>
                  <span className="inspect-v">
                    {selectedPage.internalLinkCount} internal, {selectedPage.externalLinkCount} external
                  </span>
                </div>
              </div>

              <div className="inspect-block">
                <span className="inspect-k">Title Tag ({selectedPage.title?.length ?? 0} chars):</span>
                <p className="inspect-v-block">{selectedPage.title ?? 'No title tag'}</p>
              </div>

              <div className="inspect-block">
                <span className="inspect-k">Meta Description ({selectedPage.metaDescription?.length ?? 0} chars):</span>
                <p className="inspect-v-block">{selectedPage.metaDescription ?? 'No meta description'}</p>
              </div>

              <div className="inspect-block">
                <span className="inspect-k">Canonical URL:</span>
                <p className="inspect-v-block">{selectedPage.canonicalUrl ?? 'None declared'}</p>
              </div>

              <div className="inspect-block">
                <span className="inspect-k">Structured Data (Schema.org):</span>
                <p className="inspect-v-block">
                  {selectedPage.hasStructuredData ? '✅ Present' : '❌ None detected'}
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setSelectedPage(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
